import { useRef, useState } from 'react';
import { Maximize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';

interface VideoPlayerProps {
  url: string;
  title: string;
  className?: string;
}

function VideoPlayer(props: VideoPlayerProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoading, setIsVideoLoading] = useState(true);

  async function handleFullscreen() {
    const fullscreenTarget = videoRef.current ?? containerRef.current;
    await fullscreenTarget?.requestFullscreen?.();
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex min-h-[220px] w-full items-center justify-center overflow-hidden rounded-lg bg-black sm:min-h-[360px]',
        props.className
      )}
    >
      {isVideoLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black text-sm font-medium text-white/80">
          {t('video.loading')}
        </div>
      )}
      <video
        ref={videoRef}
        className="h-full max-h-[78vh] w-full bg-black object-contain"
        src={props.url}
        title={props.title}
        controls
        preload="metadata"
        onCanPlay={() => setIsVideoLoading(false)}
        onLoadedData={() => setIsVideoLoading(false)}
        onWaiting={() => setIsVideoLoading(true)}
      />
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="absolute right-3 top-3 z-20 bg-white/90 text-slate-950 shadow-md hover:bg-white"
        onClick={handleFullscreen}
        title={t('video.fullscreen')}
        aria-label={t('video.fullscreen')}
      >
        <Maximize2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default VideoPlayer;
